import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { PhoneCall } from "lucide-react";
import { mockProducts, buildWhatsAppLink } from "../../../mock";

// SECTION: FormSection
// Tujuan utama:
// - Mengumpulkan data lead (nama, WA, lokasi, paket, catatan)
// - Saat ini: simpan ke localStorage + redirect ke /konfirmasi (MOCK, TANPA BACKEND)
// - Nanti kamu bisa ganti penyimpanan ke API FastAPI (POST /api/leads) di fungsi handleSubmit

const FormSection = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [paket, setPaket] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Validasi minimal: nama & nomor WA tidak boleh kosong
  const isValid = useMemo(() => {
    return name.trim() !== "" && phone.trim() !== "";
  }, [name, phone]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;

    const payload = { name, phone, location, paket, notes };
    setIsSubmitting(true);

    // ====== TEMPORARY MOCK FLOW ======
    // Simpan ke localStorage agar tetap ada jejak di browser user
    // NANTI: blok ini bisa kamu ganti dengan axios/fetch ke backend-mu
    try {
      localStorage.setItem("cineurasa_last_lead", JSON.stringify(payload));
    } catch (err) {
      // ignore localStorage error di flow MOCK
      console.error("localStorage error", err);
    }

    const params = new URLSearchParams(payload).toString();

    // Simulasi delay singkat sebelum redirect ke halaman konfirmasi
    setTimeout(() => {
      setIsSubmitting(false);
      navigate(`/konfirmasi?${params}`);
    }, 600);
  };

  // Tombol alternatif: langsung buat link WA dari data yang sudah diisi user
  const handleWhatsAppDirect = () => {
    const url = buildWhatsAppLink({ name, phone, location, paket, notes });
    window.open(url, "_blank");
  };

  return (
    <section id="form-pemesanan" className="mb-12 md:mb-16">
      <Card className="p-5 border shadow-md rounded-3xl border-border bg-card/90 md:p-7">
        {/* HEADER */}
        <header className="mb-4 md:mb-5">
          <h2 className="text-lg font-semibold tracking-tight text-foreground md:text-xl">
            Siap Masak? Tinggal Isi, Kami Hubungi via WhatsApp
          </h2>
          <p className="mt-1 text-xs text-muted-foreground md:text-sm">
            Tanpa ribet • Tanpa komitmen • Fast response
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:gap-6"
        >
          {/* LEFT */}
          <div className="space-y-3 text-xs md:text-sm">
            {/* Nama */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-muted-foreground md:text-xs">
                Nama lengkap
              </label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Rina Putri"
                className="text-xs h-9 border-border bg-secondary/60 md:h-10 md:text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
              />
            </div>

            {/* WhatsApp */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-muted-foreground md:text-xs">
                Nomor WhatsApp
              </label>
              <Input
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Contoh: 08xxxxxxxxxx"
                className="text-xs h-9 border-border bg-secondary/60 md:h-10 md:text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
              />
            </div>

            {/* Lokasi */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-muted-foreground md:text-xs">
                Lokasi kamu
              </label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Kota / Kecamatan"
                className="text-xs h-9 border-border bg-secondary/60 md:h-10 md:text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
              />
            </div>

            {/* Paket */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-muted-foreground md:text-xs">
                Pilihan bumbu / paket
              </label>
              <Select value={paket} onValueChange={setPaket}>
                <SelectTrigger className="text-xs h-9 border-border bg-secondary/60 md:h-10 md:text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary">
                  <SelectValue placeholder="Pilih salah satu" />
                </SelectTrigger>
                <SelectContent className="text-xs md:text-sm">
                  {mockProducts.map((product) => (
                    <SelectItem key={product.id} value={product.name}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex flex-col justify-between gap-3 text-xs md:text-sm">
            {/* Catatan */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-medium text-muted-foreground md:text-xs">
                Catatan tambahan (opsional)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tulis preferensi rasa, jumlah porsi, atau pertanyaan kamu di sini."
                className="min-h-[88px] border-border bg-secondary/60 text-xs md:min-h-[108px] md:text-sm
              focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
              />
            </div>

            {/* CTA BOX */}
            <div className="space-y-2 rounded-2xl bg-gradient-to-br from-secondary via-background to-accent px-3.5 py-3 shadow-md">
              <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-primary">
                Langkah berikutnya
              </p>
              <p className="text-xs text-foreground md:text-sm">
                Klik tombol di bawah, data kamu akan kami teruskan ke WhatsApp
                admin untuk dibantu proses pemesanan.
              </p>

              <div className="flex flex-col gap-2 pt-1.5 md:flex-row md:items-center">
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full
                bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-sm
                transition-transform duration-200 ease-out
                hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-md
                active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? "Memproses..." : "Lanjutkan ke WhatsApp"}
                </Button>

                <button
                  type="button"
                  onClick={handleWhatsAppDirect}
                  className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium
                text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
                >
                  <PhoneCall className="h-3.5 w-3.5" />
                  <span>Atau langsung chat admin</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </Card>
    </section>
  );
};

export default FormSection;
